import { Link } from 'wouter';
import { shortDate } from '../lib/format';
import type { CustomerOrder } from '../types';

/**
 * Что ждёт ответа заказчика. Одно действие, одна кнопка, во всю ширину —
 * заказчик заходит сюда не «поработать со списком», а ответить и уйти. Всё
 * остальное — заказы в работе, оплаты, переписка — ниже и мельче.
 */
export function CustomerHero({ order }: { order: CustomerOrder }) {
  const waiting = waitingFor(order);

  if (!waiting) {
    return null;
  }

  return (
    <Link href={`/my/orders/${order.id}`} className="plain-link">
      <article className="hero-card">
        {/*
         * Превью во всю ширину: по картинке сразу видно, о чём речь, — куда
         * быстрее, чем по названию заказа и номеру.
         */}
        <div className="hero-card__preview">
          {order.previewUrl ? (
            <img src={order.previewUrl} alt="" loading="lazy" />
          ) : (
            <span className="hero-card__blank" aria-hidden="true" />
          )}
          <span className="hero-card__chip">Нужен ваш ответ</span>
        </div>

        <div className="hero-card__body">
          <div className="card-row">
            <strong>{order.artist.displayName}</strong>
            <span className="meta">{order.publicNumber}</span>
          </div>
          <p>{waiting.text}</p>
          <span className="button button--primary hero-card__action">{waiting.action}</span>
        </div>
      </article>
    </Link>
  );
}

/**
 * Чего именно от заказчика ждут. Порядок важен: сначала то, что останавливает
 * работу художника, потом то, что просто ждёт.
 */
function waitingFor(order: CustomerOrder): { text: string; action: string } | null {
  if (order.canApproveSketch) {
    return {
      text: 'Скетч ждёт вашего согласования — художник не начнёт чистовую работу без него.',
      action: 'Посмотреть скетч',
    };
  }

  if (order.canAgreeDeadline) {
    return {
      text: `Художник назначил срок ${shortDate(order.deadline)} — подтвердите, что он вам подходит.`,
      action: 'Посмотреть срок',
    };
  }

  if (order.canConfirmDelivery) {
    return {
      text: 'Работа готова и передана — подтвердите, что всё получили.',
      action: 'Принять работу',
    };
  }

  return null;
}
